import React, { useEffect, useState, useRef } from "react";
import { FaStar } from "react-icons/fa";
import MapIcon from "../../images/svgs/MapIcon";
import BookSessionForm from "../forms/BookSessionForm";
import Modal from "../modal/Modal";
import Edit from "../../images/svgs/Edit";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../../services/mentorProfile";
import dummyImage from "../../images/background/dummyProfilebg.png";
import Skeleton from "react-loading-skeleton";
import {
  AILiteracySkills,
  appliedAISkills,
  AutoMLNoCodeLowCodeAIPlatforms,
  BusinessStrategyDesignRoles,
  CommunicationSkills,
  Compliance,
  ComputerVisionTools,
  CreativeInterdisciplinaryRoles,
  CriticalThinkingProblemSolving,
  DataScienceVisualizationTools,
  EmergingAI,
  EthicsSafetyGovernanceRoles,
  GenerativeAITools,
  industries,
  InfrastructureMLOpsRoles,
  InterpersonalTeamworkSkills,
  LeadershipInfluence,
  MachineLearningDeepLearningPlatforms,
  MLOpsDeploymentTools,
  NaturalLanguageProcessingTools,
  ResponsibleAI,
  softAISkills,
  strategicAISkills,
  technicalAISkills,
  TechnicalDevelopmentRoles,
} from "../../constants/constants";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";

const LabelValue = ({ label, value }) => {
  return (
    <div className="flex flex-col items-left text-[#212529]">
      <span className="font-semibold">{label}</span>
      <span className="text-gray-600 mt-2">{value}</span>{" "}
    </div>
  );
};

const generalLabels = [
  {
    value: "career",
    label: "Career Advancement/Transition",
  },
  { value: "employment", label: "Employment/Job Search" },
  { value: "resume", label: "Resume Optimization" },
  {
    value: "linkdin",
    label: "LinkedIn Profile Optimization",
  },
];
const MentorProfileSettings = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [review, setReview] = useState(null);
  const location = useLocation();
  const currentPath = location.pathname;
  const basePath = currentPath.substring(0, currentPath.lastIndexOf("/") + 1);
  const navigate = useNavigate();
  const mentorProfile = useSelector((state) => state.mentor);
  const loggedInMentorId = useSelector((state) => state.auth.id);
  const [userData, setUserData] = useState(null);
  const { userId } = useParams(); // Extract 'id' from the URL
  const dispatch = useDispatch();
  const isFetchingRef = useRef(false);

  const getMentorReview = async (mentorId) => {
    try {
      const res = await api1.get(
        `${API_ENDPOINTS.GET_MENTOR_REVIEW}/${mentorId}`
      );
      setReview(res.data);
    } catch (error) {
      console.error("Error fetching reviews for mentor:", mentorId, error);
    }
  };

  useEffect(() => {
    if (currentPath === "/dashboard/profile-settings" && loggedInMentorId && !isFetchingRef.current) {
      // Always fetch the logged-in mentor's own profile data for profile settings
      const fetchLoggedInMentorData = async () => {
        isFetchingRef.current = true;
        try {
          const data = await dispatch(getProfile({ userId: loggedInMentorId }));
          getMentorReview(data.userId);
          setUserData(data);
        } finally {
          isFetchingRef.current = false;
        }
      };
      fetchLoggedInMentorData();
    } else if (!userData && userId && !isFetchingRef.current) {
      const fetchSpecificProfileData = async (userId) => {
        isFetchingRef.current = true;
        try {
          const data = await dispatch(getProfile({ userId }));
          getMentorReview(data.userId);
          setUserData(data);
        } finally {
          isFetchingRef.current = false;
        }
      };
      fetchSpecificProfileData(userId);
    }
  }, [currentPath, loggedInMentorId, dispatch, userId]);

  useEffect(() => {
    const fetch = async () => {
      try {
        await dispatch(getProfile({ userId }));
      } catch (error) {
        console.error("Error fetching profile: ", error);
      }
    };
    if (userId) {
      fetch();
    }
  }, [dispatch, userId]);

  return (
    <div className="bg-white min-h-screen p-6 rounded-xl shadow-md">
      <div className=" mx-auto">
        {/* Profile Header */}
        <div className="bg-white shadow-md rounded-lg p-6 flex justify-between flex-col md:flex-row items-center">
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center gap-2">
              <img
                src={userData?.profileImage || dummyImage}
                alt="profileImage"
                className="w-20 h-20 border flex items-center justify-center text-xl font-bold rounded-full object-contain"
              />
              <div className="mt-2 sm:mt-0 flex">
                {Array.from({ length: 5 }, (_, i) => (
                  <span
                    key={i}
                    className={`text-lg ${
                      i < Math.round(review?.averageRating)
                        ? "text-yellow-500"
                        : "text-gray-300"
                    }`}
                  >
                    <FaStar />
                  </span>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-1 flex gap-1 items-center">
                <MapIcon /> {userData?.state}, {userData?.country}
              </p>
            </div>
            <div>
              <h2 className="text-xl font-semibold">
                {userData ? (
                  `${userData.firstName} ${userData.lastName}`
                ) : (
                  <Skeleton width={120} />
                )}
              </h2>
              <p className="text-gray-600">
                {userData?.higherEducationLevel
                  ?.replace(/_/g, " ") // Replace hyphens with spaces
                  ?.replace(/^./, (c) => c.toUpperCase()) || (
                  <Skeleton width={100} />
                )}
              </p>
            </div>
          </div>
          <div className="text-right flex flex-col items-center">
            <div className="text-xl font-bold text-custom-blue">
              {userData?.mentorMeeting ? (
                <div className="items-center">
                  {userData?.mentorMeeting["60minmeeting"] || 0}$
                  <span className="text-sm">/Hour</span>
                </div>
              ) : (
                <Skeleton width={50} height={24} />
              )}
            </div>

            <div className="relative">
              {localStorage.getItem("userType") === "user" && (
                <button
                  className="bg-custom-blue text-white px-6 py-2 rounded-full mt-2 text-sm md:text-xl 
                      hover:bg-blue-700 transition duration-300 ease-in-out transform hover:scale-105"
                  onClick={() => setIsOpen(true)}
                >
                  Book a Session
                </button>
              )}
              {basePath === "/dashboard/book-appointment/" ||
                (localStorage.getItem("userType") === "mentor" && (
                  <button
                    className="bg-custom-blue text-white px-6 py-2 rounded-full mt-2 text-sm md:text-xl 
                        flex items-center gap-2 hover:bg-blue-700 transition duration-300 ease-in-out 
                        transform hover:scale-105"
                    onClick={() => {
                      navigate(
                        `/dashboard/edit/${mentorProfile.profile.userId}`
                      );
                    }}
                  >
                    <Edit className="w-6" /> Edit Profile
                  </button>
                ))}

              {mentorProfile?.profile && (
                <Modal
                  isOpen={isOpen}
                  onClose={() => setIsOpen(false)}
                  width="w-[700px]"
                  label={"Book a Session"}
                >
                  <BookSessionForm
                    timeZone={mentorProfile.profile.timezone}
                    setIsOpen={setIsOpen}
                    userData={userData}
                  />
                </Modal>
              )}
            </div>
          </div>
        </div>

        {/* About Me Section */}
        <div className="bg-white shadow-md rounded-lg p-6 mt-6 border-b-2 borber-b border-custom-blue">
          <h3 className="text-lg font-semibold">About Me</h3>
          <div className="border-b pt-4"></div>
          <p className="text-gray-600 mt-2">
            {userData?.bio || <Skeleton count={3} />}
          </p>
        </div>

        {/* Personal Details */}
        <div className="bg-white shadow-md rounded-lg p-6 mt-6 border-b-2  borber-b border-custom-blue">
          <h3 className="text-lg font-semibold">Personal Details</h3>
          <div className="border-b pt-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 text-gray-600">
            <LabelValue
              label={"Gender"}
              value={
                userData?.gender === 0
                  ? "Male"
                  : "Female" || <Skeleton width={80} />
              }
            />
            <LabelValue
              label={"Country"}
              value={userData?.country || <Skeleton width={80} />}
            />
            <LabelValue
              label={"City"}
              value={userData?.city || <Skeleton width={80} />}
            />
          </div>
        </div>
        <div className="bg-white shadow-md rounded-lg p-6 mt-6 border-b-2  borber-b border-custom-blue">
          <h3 className="text-lg font-semibold">Professional Background</h3>
          <div className="border-b pt-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 text-gray-600">
            <LabelValue
              label={"Current Occupation"}
              value={userData?.currentJobOccupation}
            />
            <LabelValue
              label={"Professional Experience"}
              value={
                typeof userData?.professionalExperience === "string" ? (
                  userData.professionalExperience
                    .replace(/_/g, " ")
                    .replace(/^./, (c) => c.toUpperCase())
                ) : (
                  <Skeleton width={80} />
                )
              }
            />
            <LabelValue
              label={"Years for Work Experience"}
              value={
                typeof userData?.yearOfExperience === "string" ? (
                  userData.yearOfExperience
                    .replace(/_/g, " ")
                    .replace(/^./, (c) => c.toUpperCase())
                ) : (
                  <Skeleton width={80} />
                )
              }
            />
          </div>
        </div>

        {/* Skills */}
        <div className="bg-white shadow-md rounded-lg p-6 mt-6 border-b-2  borber-b border-custom-blue">
          <h3 className="text-lg font-semibold">Mentoring Focus Areas</h3>
          <div className="border-b pt-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 text-gray-600">
            {userData?.general && (
              <LabelValue
                label={"General Career Mentoring/Coaching "}
                value={userData?.general
                  ?.map(
                    (val) =>
                      generalLabels.find((skill) => skill.value === val)?.label
                  )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.AILiteracySkills && (
              <LabelValue
                label={"Foundational AI Literacy (For Everyone)"}
                value={userData?.AILiteracySkills?.map(
                  (val) =>
                    AILiteracySkills.find((skill) => skill.value === val)?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.appliedAISkills && (
              <LabelValue
                label={"Applied AI Skills (For Business & Industry Roles)"}
                value={userData?.appliedAISkills
                  ?.map(
                    (val) =>
                      appliedAISkills.find((skill) => skill.value === val)
                        ?.label
                  )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.ethicsResponsibleAI && (
              <LabelValue
                label={"AI Ethics & Responsible AI"}
                value={userData?.ethicsResponsibleAI
                  ?.map(
                    (val) =>
                      ResponsibleAI.find((skill) => skill.value === val)?.label
                  )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.softAISkills && (
              <LabelValue
                label={"Soft Skills for the AI Era"}
                value={userData?.softAISkills
                  ?.map(
                    (val) =>
                      softAISkills.find((skill) => skill.value === val)?.label
                  )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.strategicAISkills && (
              <LabelValue
                label={"Strategic AI Skills"}
                value={userData?.strategicAISkills
                  ?.map(
                    (val) =>
                      strategicAISkills.find((skill) => skill.value === val)
                        ?.label
                  )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.technicalAISkills && (
              <LabelValue
                label={"Technical AI Skills for Practioners and Developers"}
                value={userData?.technicalAISkills
                  ?.map(
                    (val) =>
                      technicalAISkills.find((skill) => skill.value === val)
                        ?.label
                  )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.TechnicalDevelopmentRoles && (
              <LabelValue
                label={"Technical & Development Roles"}
                value={userData?.TechnicalDevelopmentRoles?.map(
                  (val) =>
                    TechnicalDevelopmentRoles.find(
                      (skill) => skill.value === val
                    )?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.InfrastureRoles && (
              <LabelValue
                label={"Infrastructure & MLOps Roles"}
                value={userData?.InfrastureRoles?.map(
                  (val) =>
                    InfrastructureMLOpsRoles.find(
                      (skill) => skill.value === val
                    )?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.ethicsRoles && (
              <LabelValue
                label={"Ethics, Safety & Governance Roles"}
                value={userData?.ethicsRoles
                  ?.map(
                    (val) =>
                      EthicsSafetyGovernanceRoles.find(
                        (skill) => skill.value === val
                      )?.label
                  )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.buisnessRole && (
              <LabelValue
                label={"Business, Strategy & Design Roles"}
                value={userData?.buisnessRole
                  ?.map(
                    (val) =>
                      BusinessStrategyDesignRoles.find(
                        (skill) => skill.value === val
                      )?.label
                  )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.CreativeRole && (
              <LabelValue
                label={"Creative & Interdisciplinary Roles"}
                value={userData?.CreativeRole?.map(
                  (val) =>
                    CreativeInterdisciplinaryRoles.find(
                      (skill) => skill.value === val
                    )?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.Compliance && (
              <LabelValue
                label={"Compliance"}
                value={userData?.Compliance?.map(
                  (val) =>
                    Compliance.find((skill) => skill.value === val)?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.EmergingAI && (
              <LabelValue
                label={"Emerging AI"}
                value={userData?.EmergingAI?.map(
                  (val) =>
                    EmergingAI.find((skill) => skill.value === val)?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.MachineLearningDeepLearningPlatforms && (
              <LabelValue
                label={"Machine Learning & Deep Learning Platforms"}
                value={userData?.MachineLearningDeepLearningPlatforms?.map(
                  (val) =>
                    MachineLearningDeepLearningPlatforms.find(
                      (skill) => skill.value === val
                    )?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.AutoMLNoCodeLowCodeAIPlatforms && (
              <LabelValue
                label={"AutoML & No-Code/Low-Code AI Platforms"}
                value={userData?.AutoMLNoCodeLowCodeAIPlatforms?.map(
                  (val) =>
                    AutoMLNoCodeLowCodeAIPlatforms.find(
                      (skill) => skill.value === val
                    )?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.NaturalLanguageProcessingTools && (
              <LabelValue
                label={"Natural Language Processing (NLP) Tools"}
                value={userData?.NaturalLanguageProcessingTools?.map(
                  (val) =>
                    NaturalLanguageProcessingTools.find(
                      (skill) => skill.value === val
                    )?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.ComputerVisionTools && (
              <LabelValue
                label={"Computer Vision Tools"}
                value={userData?.ComputerVisionTools?.map(
                  (val) =>
                    ComputerVisionTools.find((skill) => skill.value === val)
                      ?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.GenerativeAITools && (
              <LabelValue
                label={"Generative AI Tools"}
                value={userData?.GenerativeAITools?.map(
                  (val) =>
                    GenerativeAITools.find((skill) => skill.value === val)
                      ?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.MLOpsDeploymentTools && (
              <LabelValue
                label={"MLOps & Deployment Tools"}
                value={userData?.MLOpsDeploymentTools?.map(
                  (val) =>
                    MLOpsDeploymentTools.find((skill) => skill.value === val)
                      ?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.DataScienceVisualizationTools && (
              <LabelValue
                label={"Data Science & Visualization Tools"}
                value={userData?.DataScienceVisualizationTools?.map(
                  (val) =>
                    DataScienceVisualizationTools.find(
                      (skill) => skill.value === val
                    )?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.CommunicationSkills && (
              <LabelValue
                label={"Communication Skills"}
                value={userData?.CommunicationSkills?.map(
                  (val) =>
                    CommunicationSkills.find((skill) => skill.value === val)
                      ?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.InterpersonalTeamworkSkills && (
              <LabelValue
                label={"Interpersonal & Teamwork Skills"}
                value={userData?.InterpersonalTeamworkSkills?.map(
                  (val) =>
                    InterpersonalTeamworkSkills.find(
                      (skill) => skill.value === val
                    )?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.CriticalThinkingProblemSolving && (
              <LabelValue
                label={"Critical Thinking & Problem Solving"}
                value={userData?.CriticalThinkingProblemSolving?.map(
                  (val) =>
                    CriticalThinkingProblemSolving.find(
                      (skill) => skill.value === val
                    )?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}
            {userData?.LeadershipInfluence && (
              <LabelValue
                label={"Leadership & Influence"}
                value={userData?.LeadershipInfluence?.map(
                  (val) =>
                    LeadershipInfluence.find((skill) => skill.value === val)
                      ?.label
                )
                  .filter(Boolean) // remove undefined if any values don't match
                  .join(", ")}
              />
            )}

            {userData?.ai_application && (
              <LabelValue
                label={"AI Application/Transformation by Industry"}
                value={userData?.ai_application
                  ?.map(
                    (data, index, array) =>
                      industries.find((ai) => ai.value === data)?.label +
                      (index < array.length - 1 ? ", " : "")
                  )
                  .join("")}
              />
            )}
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white shadow-md rounded-lg p-6 mt-6 border-b-2  borber-b border-custom-blue">
          <h3 className="text-lg font-semibold">Pricing</h3>
          <div className="border-b pt-4"></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2 text-gray-600">
            {[
              "15minmeeting",
              "30minmeeting",
              "45minmeeting",
              "60minmeeting",
            ].map((key) => (
              <LabelValue
                key={key}
                label={key.replace("minmeeting", " min")}
                value={
                  userData?.mentorMeeting?.[key] !== undefined &&
                  userData?.mentorMeeting?.[key] !== null
                    ? `$${userData.mentorMeeting[key]}`
                    : "N/A"
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorProfileSettings;
